import React, { memo, useMemo } from 'react';
import { Chart } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';

import * as C from './Charts.styled';
import {
  CHART_GRID,
  CHART_INK,
  CHART_NO_ANIMATION,
  CHART_TICK_FONT,
  chartBaseTooltip,
  chartIntegerTicks,
  chartLegend,
} from './chartTokens';
import { registerCharts } from './registerCharts';

registerCharts();

/** Ряд столбиков: подпись, значения (`null` — нет данных), цвета. */
export interface IDualBarSeries {
  label: string;
  data: ReadonlyArray<number | null>;
  /** Цвет; массив — свой у каждого столбика (бледный — неполный месяц). */
  color: string | ReadonlyArray<string>;
  format: (value: number) => string;
  /** Счётчик — только целые отметки оси. */
  integer?: boolean;
}

/**
 * Второй ряд мелкий — меньше этой доли от максимума первого: тогда два
 * графика друг над другом с общей осью X, иначе — сдвоенные столбики на
 * одной оси (пакет 2, P3: две шкалы без названий вводили в заблуждение).
 */
export const DUAL_BAR_SPLIT_RATIO = 0.3;

const maxOf = (data: ReadonlyArray<number | null>): number =>
  data.reduce<number>((acc, value) => Math.max(acc, value ?? 0), 0);

/** Делить ли на два графика: второй ряд мелкий рядом с первым. */
export const dualBarSplit = (
  top: ReadonlyArray<number | null>,
  bottom: ReadonlyArray<number | null>,
  ratio: number = DUAL_BAR_SPLIT_RATIO,
): boolean => {
  const topMax = maxOf(top);
  const bottomMax = maxOf(bottom);
  return topMax > 0 && bottomMax < topMax * ratio;
};

export interface IDualBarDataset {
  type: 'bar';
  label: string;
  data: Array<number | null>;
  backgroundColor: string | string[];
  borderRadius: number;
  yAxisID: 'y' | 'y2';
  /** На двух графиках столбик занимает всю ширину своей панели. */
  grouped: boolean;
}

/** Чистый построитель: ряды → датасеты и раскладка осей. */
export const dualBarData = (
  labels: ReadonlyArray<string>,
  top: IDualBarSeries,
  bottom: IDualBarSeries,
  split: boolean,
): { labels: string[]; datasets: IDualBarDataset[] } => {
  const dataset = (
    series: IDualBarSeries,
    axis: 'y' | 'y2',
  ): IDualBarDataset => ({
    type: 'bar',
    label: series.label,
    data: labels.map((_, index) => series.data[index] ?? null),
    backgroundColor:
      typeof series.color === 'string' ? series.color : [...series.color],
    borderRadius: 2,
    yAxisID: axis,
    grouped: !split,
  });
  // Два графика: chart.js кладёт стопку осей снизу вверх в порядке
  // объявления — нижний (второй ряд) на `y`, верхний (первый) на `y2`.
  return {
    labels: [...labels],
    datasets: split
      ? [dataset(top, 'y2'), dataset(bottom, 'y')]
      : [dataset(top, 'y'), dataset(bottom, 'y')],
  };
};

export interface IDualBarChartProps {
  labels: ReadonlyArray<string>;
  /** Полные подписи точек в подсказке («10.2026»). */
  titles?: ReadonlyArray<string>;
  top: IDualBarSeries;
  bottom: IDualBarSeries;
  /** Два графика друг над другом; не задано — по `dualBarSplit`. */
  split?: boolean;
  /** Подпись «нет данных» — вместо прочерка у месяца без данных. */
  emptyLabel: string;
  /** Строка подсказки точки снизу («конверсия 12 %»). */
  footerOf?: (index: number) => string | null;
  /** Приписка к подсказке точки («по 09.10»). */
  noteOf?: (index: number) => string | null;
  ariaLabel?: string;
}

/**
 * ТЗ 0103, пакет 2 — два счётчика по одной оси времени (лиды и продажи по
 * месяцам): сдвоенные столбики на ОДНОЙ оси, а если второй ряд мелкий —
 * два графика один над другим с общей осью X, у каждого своя подписанная
 * шкала. Без линий и сглаживания: между месяцами значений нет.
 */
export const DualBarChart = memo(
  ({
    labels,
    titles,
    top,
    bottom,
    split: splitProp,
    emptyLabel,
    footerOf,
    noteOf,
    ariaLabel,
  }: IDualBarChartProps) => {
    const split = splitProp ?? dualBarSplit(top.data, bottom.data);
    const data = useMemo(() => dualBarData(labels, top, bottom, split), [
      labels,
      top,
      bottom,
      split,
    ]);
    const options: ChartOptions<'bar'> = useMemo(() => {
      const axis = (series: IDualBarSeries) => ({
        beginAtZero: true,
        grid: { color: CHART_GRID },
        title: {
          display: split,
          text: series.label,
          color: CHART_INK,
          font: CHART_TICK_FONT,
        },
        ticks: {
          font: CHART_TICK_FONT,
          color: CHART_INK,
          ...chartIntegerTicks(Boolean(series.integer)),
          callback: (value: string | number) => series.format(Number(value)),
        },
      });
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: CHART_NO_ANIMATION,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: chartLegend,
          tooltip: {
            ...chartBaseTooltip,
            callbacks: {
              title: (items) => {
                const [first] = items;
                if (!first) return '';
                const title =
                  titles?.[first.dataIndex] ?? String(first.label ?? '');
                const note = noteOf ? noteOf(first.dataIndex) : null;
                return note ? `${title} · ${note}` : title;
              },
              label: (item) => {
                const raw = item.raw as number | null;
                const series = item.datasetIndex === 0 ? top : bottom;
                // Порядок рядов в подсказке — как в данных: первый, второй.
                return `${item.dataset.label}: ${
                  raw === null ? emptyLabel : series.format(Number(raw))
                }`;
              },
              footer: (items) => {
                const [first] = items;
                if (!first || !footerOf) return '';
                return footerOf(first.dataIndex) ?? '';
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: CHART_TICK_FONT, color: CHART_INK },
          },
          // Два графика — стопкой одной колонки осей поровну: сверху первый
          // ряд (`y2`), снизу второй (`y`); ось X общая.
          y: split
            ? { ...axis(bottom), stack: 'dual', stackWeight: 1, offset: true }
            : axis(top),
          ...(split
            ? {
                y2: {
                  ...axis(top),
                  position: 'left' as const,
                  stack: 'dual',
                  stackWeight: 1,
                  offset: true,
                },
              }
            : {}),
        },
      } as ChartOptions<'bar'>;
    }, [split, top, bottom, titles, emptyLabel, footerOf, noteOf]);
    return (
      <C.ChartBox role="img" aria-label={ariaLabel}>
        <Chart type="bar" data={data as never} options={options as never} />
      </C.ChartBox>
    );
  },
);

DualBarChart.displayName = 'DualBarChart';
